/**
 * DeliveryAddressPicker — where a delivery goes, as the delivery section
 * shows it: a summary of the chosen address (name, lines, town, postcode, and
 * "Default" when it is the customer's default delivery address) with a Change
 * button that opens the customer's addresses in `DeliveryAddressDialog`.
 * With nothing chosen yet the summary is replaced by a "Choose an address"
 * button. Used for Ship's one destination and for each Multi-destination row.
 */

import { useId, useRef, useState } from "react";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  CisOnlyBadge,
  DeliveryAddressDialog,
  type AddressCapture,
  type FetchAddressPage,
} from "./DeliveryAddressDialog";
import { postcodeLine, type PickerAddress } from "./pickerAddress";

export function DeliveryAddressPicker({
  label,
  value,
  onChoose,
  fetchPage,
  capture,
  changeLabel,
  chooseLabel,
  isInvalid = false,
  describedBy,
  disabled,
}: {
  /** The visible label above the summary; omitted where the row names it (a Multi-destination row). */
  label?: string;
  /** The chosen address; null until one is. */
  value: PickerAddress | null;
  onChoose: (address: PickerAddress) => void;
  fetchPage: FetchAddressPage;
  /** Absent where the book offers no capture. */
  capture: AddressCapture | null;
  /** The Change button's accessible name: unique where several pickers share a page. */
  changeLabel: string;
  /** The Choose button's accessible name; its text when absent. */
  chooseLabel?: string;
  /** The server refused the address: the picker is marked invalid, and its words are shown by the caller. */
  isInvalid?: boolean;
  /** The id of the element carrying the server's words. */
  describedBy?: string;
  disabled: boolean;
}) {
  const labelId = useId();
  const openerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const dialog = (
    <DeliveryAddressDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      chosenId={value?.id ?? null}
      fetchPage={fetchPage}
      capture={capture}
      onChoose={onChoose}
      returnFocusTo={openerRef}
      disabled={disabled}
    />
  );
  const invalidProps = {
    "aria-invalid": isInvalid ? true : undefined,
    "aria-describedby": describedBy,
  } as const;

  return (
    <div className="space-y-1.5">
      {label && (
        <p id={labelId} className="block text-sm font-medium">
          {label}
        </p>
      )}
      {value === null ? (
        <div>
          <Button
            ref={openerRef}
            type="button"
            variant="outline"
            aria-label={chooseLabel}
            disabled={disabled}
            onClick={() => setIsOpen(true)}
            {...invalidProps}
          >
            Choose an address
          </Button>
        </div>
      ) : (
        <div
          role="group"
          aria-labelledby={label ? labelId : undefined}
          className={`flex items-start justify-between gap-3 rounded-md border p-3 ${isInvalid ? "border-destructive" : ""}`}
        >
          <div className="min-w-0 text-sm">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium">{value.name}</span>
              {value.isDefault && <Badge variant="secondary">Default</Badge>}
              {value.isCisOnly && <CisOnlyBadge />}
            </p>
            {value.address && (
              <p className="text-muted-foreground">{value.address}</p>
            )}
            <p className="text-muted-foreground">{postcodeLine(value)}</p>
          </div>
          <Button
            ref={openerRef}
            type="button"
            variant="outline"
            size="sm"
            aria-label={changeLabel}
            disabled={disabled}
            onClick={() => setIsOpen(true)}
            {...invalidProps}
          >
            Change
          </Button>
        </div>
      )}
      {dialog}
    </div>
  );
}
